import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useCoupons } from "@/Coupons/hooks/use-coupons";
import { CouponCard } from "@/Coupons/components/CouponCard";
import { ClipToastProvider, useClipToast } from "@/Coupons/context/ClipToastContext";
import { IconAlertCircle, IconCircleCheck, IconX } from '@tabler/icons-react';

// Toast component for clip notifications (same as Coupons page)
const ClipToastNotification = () => {
  const { toastState, hideToast } = useClipToast();

  if (!toastState.show) return null;

  const isSuccess = toastState.type === 'success';

  return (
    <div className="fixed top-4 right-4 z-50 w-full max-w-sm animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="bg-white rounded-lg shadow-lg border border-border overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg flex-shrink-0 w-9 h-9 ${isSuccess ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              {isSuccess ? (
                <IconCircleCheck className="h-5 w-5 text-emerald-500" />
              ) : (
                <IconAlertCircle className="h-5 w-5 text-red-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-foreground">
                {isSuccess ? 'Coupon Saved!' : 'Unable to Save'}
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                {toastState.message}
              </p>
            </div>
            <button
              onClick={hideToast}
              className="p-1 rounded-md hover:bg-muted flex-shrink-0"
            >
              <IconX className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CouponsSectionProps {
  className?: string;
  filterType?: string;
  excludeType?: string;
  limit?: number;
  title?: string;
  subtitle?: string;
}

// Fisher-Yates shuffle
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const CouponsSectionContent = ({
  className = '',
  filterType,
  excludeType,
  limit = 5,
  title = 'Digital Coupons',
  subtitle = 'Clip coupons and save on your next shopping trip'
}: CouponsSectionProps) => {
  const { data: coupons, isLoading, error } = useCoupons({ pageSize: 1000, sortBy: 'expires' });

  // Filter by type (include or exclude), then randomly select up to limit
  let filteredCoupons = coupons || [];
  if (filterType) {
    filteredCoupons = filteredCoupons.filter((c) => c.category === filterType);
  }
  if (excludeType) {
    filteredCoupons = filteredCoupons.filter((c) => c.category !== excludeType);
  }

  const displayCoupons = filteredCoupons.length > limit
    ? shuffleArray(filteredCoupons).slice(0, limit)
    : filteredCoupons.slice(0, limit);

  return (
    <section className={`py-12 bg-neutral-50 ${className}`.trim()}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold mb-2 text-foreground">{title}</h2>
            <p className="text-muted-foreground">{subtitle}</p>
          </div>
          <Link
            to="/coupons"
            className="hidden sm:flex flex-none rounded-full bg-primary/10 px-3.5 py-1.5 text-sm font-semibold text-primary shadow-sm ring-1 ring-inset ring-primary/20 hover:bg-primary/20"
          >
            View All Offers <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <IconAlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load coupons. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        {isLoading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="w-full aspect-[16/9]" />
                <div className="p-3 space-y-2">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-7 w-full mt-1.5" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && !error && displayCoupons.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
            {displayCoupons.map((coupon) => (
              <CouponCard
                key={coupon.id || coupon.offerId}
                coupon={coupon}
              />
            ))}
          </div>
        )}

        {!isLoading && !error && displayCoupons.length === 0 && (
          <Alert>
            <IconAlertCircle className="h-4 w-4" />
            <AlertDescription>
              No coupons are currently available. Please check back later.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </section>
  );
};

// Wrapper component that provides the ClipToastContext
export const CouponsSection = (props: CouponsSectionProps) => {
  return (
    <ClipToastProvider>
      <CouponsSectionContent {...props} />
      <ClipToastNotification />
    </ClipToastProvider>
  );
};
